import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import classNames from 'clsx';

import { AppSections, Languages } from '../../constants';
import { useApplicationParams, useLanguage } from '../../context';
import CustomerSupportButton from '../shop/CustomerSupportButton';
import BackHeader from '../layout/BackHeader';
import HeaderWeb from '../layout/HeaderWeb';
import ProductItem from '../shop/ProductItem';

const SectionListing = () => {
  const routeParams = useParams();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const [section, setSection] = useState(() => ({
    id: routeParams.id,
    title: searchParams.get('title') || '',
    type: searchParams.get('type') === '2' ? 'products' : 'shops',
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [sectionProducts, setSectionProducts] = useState([]);

  const { isWeb, appVersion, platform, deviceId, userToken, appSection } = useApplicationParams();
  const { language } = useLanguage();

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();

    const requestConfig = {
      headers: {
        'Content-Type': 'application/json',
        'x-api-version': appVersion || '4.11.1',
        'x-api-key': process.env.REACT_APP_API_KEY,
        'x-api-os': platform || 'android',
        'x-user-lang': language || Languages.English,
        'x-api-device-id': deviceId || 'CBC64810-FF0C-4008-9558-A999C18085E7',
        authorization: userToken || '',
      },
      cancelToken: cancelToken.token,
    };

    const getSection = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/shopv2/sections${isWeb ? '?source=web' : ''}`,
          requestConfig,
        );

        const currentSection = response.data.data.find((s) => +s.id === +routeParams.id);

        if (currentSection) {
          setSection((prevSection) => ({ ...prevSection, ...currentSection }));
        }
      } catch (error) {
        console.error(`Error get section: ${error?.message}`);
      }
    };

    const getSectionProducts = async () => {
      try {
        setIsLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/shopv2/section/products?sectionId=${section.id}${
            isWeb ? '&source=web' : ''
          }`,
          requestConfig,
        );

        const products = response.data.data;

        if (!products.length) {
          return navigate(isWeb ? '/shop' : `/shop?userToken=${userToken}`);
        }

        if (response.data.status === 'success') {
          setSectionProducts(products);
        }
      } catch (error) {
        console.error(`Error get section products: ${error?.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (section.title === '' || isWeb) {
      // need to make an API call to get section name
      // this case happens when navigation is from app shopId param
      getSection();
    }

    getSectionProducts();

    return () => {
      cancelToken.cancel();
    };
  }, [language]);

  const isLiveRoomsSection = appSection === AppSections.LiveRoom;

  const shopProductsSectionClassName = classNames('shop-products-section', 'mt-2', {
    'handle-fixed-nav': !isLiveRoomsSection,
  });

  return routeParams.id !== '0' ? (
    <>
      {isWeb && (
        <>
          <HeaderWeb />
          <CustomerSupportButton />
        </>
      )}
      <div className="section-wrapper">
        {!isLiveRoomsSection && <BackHeader prev="/" title={section.title} />}
        <section className={shopProductsSectionClassName}>
          {isLoading ? (
            <div className="loader mt-5" />
          ) : (
            sectionProducts?.map((productItem) => (
              <Col md={3} sm={4} xs={6} className="product-col" key={`product-${productItem.id}`}>
                <ProductItem
                  data={productItem}
                  sectionId={section.id}
                  isWeb={isWeb}
                  isLiveRoomsSection={isLiveRoomsSection}
                  token={userToken}
                />
              </Col>
            ))
          )}
        </section>
      </div>
    </>
  ) : null;
};

export default SectionListing;
